// Global Instructions Rule Applied! - 2026-04-20

import { fetchAllPages } from '../../../core/utils/fetchAllPages.ts'
import { fetchCompaniesFiltered } from '../../companies/services/companiesService.ts'
import { fetchContactsFiltered, fetchContactsPaginated } from '../../contacts/services/contactsService.ts'
import { fetchDealsFiltered, fetchDealsPaginated } from '../../deals/services/dealsService.ts'
import {
  fetchFundraisingFiltered,
  fetchFundraisingPaginated
} from '../../fundraising/services/fundraisingService.ts'
import { fetchAssetTransactionsFiltered } from '../../asset_transactions/services/assetTransactionsService.ts'
import { fetchFundGroupSubscriptionsFiltered } from '../../fund_groups/services/fundGroupsService.ts'
import { fetchFundTransactionsFiltered } from '../../fund_transactions/services/fundTransactionsService.ts'
import {
  fetchFundInvestmentSummaryFiltered,
  fetchFundInvestorsFiltered,
  fetchFundsFiltered
} from '../../funds/services/fundsService.ts'
import { fetchJournalEntriesFiltered } from '../../journal_entries/services/journalEntriesService.ts'
import { fetchTranchesFiltered } from '../../tranches/services/tranchesService.ts'
import type { FilterValue as CompaniesFilterValue, SortConfig as CompaniesSortConfig } from '../../../state_manager/store_models/companies.ts'
import type { FilterValue as ContactsFilterValue, SortConfig as ContactsSortConfig } from '../../../state_manager/store_models/contacts.ts'
import type { FilterValue as DealsFilterValue, SortConfig as DealsSortConfig } from '../../../state_manager/store_models/deals.ts'
import type { FilterValue as FundraisingFilterValue, SortConfig as FundraisingSortConfig } from '../../../state_manager/store_models/fundraising.ts'
import type { FilterValue as FundsFilterValue, SortConfig as FundsSortConfig } from '../../../state_manager/interfaces/funds.ts'
import type {
  FilterValue as TableViewFilterValue,
  SortConfig as TableViewSortConfig
} from '../../global_configs/tableViewPreferences.ts'

const PAGE_SIZE = 1000

export async function fetchAllCompaniesForExport(
  sorting: CompaniesSortConfig[],
  filters: CompaniesFilterValue[],
  accessToken: string | null,
  reportId?: string | null
) {
  return fetchAllPages({
    pageSize: PAGE_SIZE,
    fetchPage: async (page, pageSize) => {
      const r = await fetchCompaniesFiltered(sorting, filters, page, pageSize, accessToken, {
        applyGlobalRules: false,
        reportId
      })
      return { data: r.data, hasMore: r.hasMore, error: r.error }
    }
  })
}

export async function fetchAllDealsForExport(
  sorting: DealsSortConfig[],
  filters: DealsFilterValue[],
  accessToken: string | null,
  reportId?: string | null
) {
  const hasFilters = filters && filters.length > 0
  const hasSorting = sorting.length > 0

  if (hasFilters || hasSorting) {
    return fetchAllPages({
      pageSize: PAGE_SIZE,
      fetchPage: async (page, pageSize) => {
        const r = await fetchDealsFiltered(sorting, filters, page, pageSize, accessToken, reportId)
        return { data: r.data, hasMore: r.hasMore, error: r.error }
      }
    })
  }

  return fetchAllPages({
    pageSize: PAGE_SIZE,
    fetchPage: async (page, pageSize) => {
      const r = await fetchDealsPaginated(page, pageSize, accessToken, reportId)
      return { data: r.data, hasMore: r.hasMore, error: r.error }
    }
  })
}

export async function fetchAllContactsForExport(
  sorting: ContactsSortConfig[],
  filters: ContactsFilterValue[],
  accessToken: string | null,
  reportId?: string | null
) {
  const hasFilters = filters && filters.length > 0
  const hasSorting = sorting.length > 0

  if (hasFilters || hasSorting) {
    return fetchAllPages({
      pageSize: PAGE_SIZE,
      fetchPage: async (page, pageSize) => {
        const r = await fetchContactsFiltered(sorting, filters, page, pageSize, accessToken, reportId)
        return { data: r.data, hasMore: r.hasMore, error: r.error }
      }
    })
  }

  return fetchAllPages({
    pageSize: PAGE_SIZE,
    fetchPage: async (page, pageSize) => {
      const r = await fetchContactsPaginated(page, pageSize, accessToken, reportId)
      return { data: r.data, hasMore: r.hasMore, error: r.error }
    }
  })
}

export async function fetchAllFundInvestmentsForExport(
  fundId: string,
  sorting: FundsSortConfig[],
  filters: FundsFilterValue[],
  accessToken: string | null
) {
  return fetchAllPages({
    pageSize: PAGE_SIZE,
    fetchPage: async (page, pageSize) => {
      const r = await fetchFundInvestmentSummaryFiltered(fundId, sorting, filters, page, pageSize, accessToken)
      return { data: r.data, hasMore: r.hasMore, error: r.error }
    }
  })
}

export async function fetchAllFundsForExport(
  sorting: FundsSortConfig[],
  filters: FundsFilterValue[],
  accessToken: string | null,
  reportId?: string | null
) {
  return fetchAllPages({
    pageSize: PAGE_SIZE,
    fetchPage: async (page, pageSize) => {
      const r = await fetchFundsFiltered(sorting, filters, page, pageSize, accessToken, { reportId })
      return { data: r.data, hasMore: r.hasMore, error: r.error }
    }
  })
}

export async function fetchAllAssetTransactionsForExport(
  sorting: TableViewSortConfig[],
  filters: TableViewFilterValue[],
  accessToken: string | null
) {
  return fetchAllPages({
    pageSize: PAGE_SIZE,
    fetchPage: async (page, pageSize) => {
      const r = await fetchAssetTransactionsFiltered(sorting, filters, page, pageSize, accessToken)
      return { data: r.data, hasMore: r.hasMore, error: r.error }
    }
  })
}

export async function fetchAllFundInvestorsForExport(
  fundId: string,
  sorting: TableViewSortConfig[],
  filters: TableViewFilterValue[],
  accessToken: string | null
) {
  return fetchAllPages({
    pageSize: PAGE_SIZE,
    fetchPage: async (page, pageSize) => {
      const r = await fetchFundInvestorsFiltered(fundId, sorting, filters, page, pageSize, accessToken)
      return { data: r.data, hasMore: r.hasMore, error: r.error }
    }
  })
}

export async function fetchAllFundGroupSubscriptionsForExport(
  fundGroupId: string,
  sorting: TableViewSortConfig[],
  filters: TableViewFilterValue[],
  accessToken: string | null
) {
  return fetchAllPages({
    pageSize: PAGE_SIZE,
    fetchPage: async (page, pageSize) => {
      const r = await fetchFundGroupSubscriptionsFiltered(fundGroupId, sorting, filters, page, pageSize, accessToken)
      return { data: r.data, hasMore: r.hasMore, error: r.error }
    }
  })
}

export async function fetchAllFundTransactionsForExport(
  sorting: TableViewSortConfig[],
  filters: TableViewFilterValue[],
  accessToken: string | null
) {
  return fetchAllPages({
    pageSize: PAGE_SIZE,
    fetchPage: async (page, pageSize) => {
      const r = await fetchFundTransactionsFiltered(sorting, filters, page, pageSize, accessToken)
      return { data: r.data, hasMore: r.hasMore, error: r.error }
    }
  })
}

export async function fetchAllJournalEntriesForExport(
  sorting: TableViewSortConfig[],
  filters: TableViewFilterValue[],
  accessToken: string | null
) {
  return fetchAllPages({
    pageSize: PAGE_SIZE,
    fetchPage: async (page, pageSize) => {
      const r = await fetchJournalEntriesFiltered(sorting, filters, page, pageSize, accessToken)
      return { data: r.data, hasMore: r.hasMore, error: r.error }
    }
  })
}

export async function fetchAllTranchesForExport(
  sorting: TableViewSortConfig[],
  filters: TableViewFilterValue[],
  accessToken: string | null
) {
  return fetchAllPages({
    pageSize: PAGE_SIZE,
    fetchPage: async (page, pageSize) => {
      const r = await fetchTranchesFiltered(sorting, filters, page, pageSize, accessToken)
      return { data: r.data, hasMore: r.hasMore, error: r.error }
    }
  })
}

export async function fetchAllFundraisingForExport(
  sorting: FundraisingSortConfig[],
  filters: FundraisingFilterValue[],
  accessToken: string | null,
  reportId?: string | null
) {
  const hasFilters = filters && filters.length > 0
  const hasSorting = sorting.length > 0

  if (hasFilters || hasSorting) {
    return fetchAllPages({
      pageSize: PAGE_SIZE,
      fetchPage: async (page, pageSize) => {
        const r = await fetchFundraisingFiltered(sorting, filters, page, pageSize, accessToken, reportId)
        return { data: r.data, hasMore: r.hasMore, error: r.error }
      }
    })
  }

  return fetchAllPages({
    pageSize: PAGE_SIZE,
    fetchPage: async (page, pageSize) => {
      const r = await fetchFundraisingPaginated(page, pageSize, accessToken, reportId)
      return { data: r.data, hasMore: r.hasMore, error: r.error }
    }
  })
}
